import { t } from "./i18n";
import { textField, selectField } from "./form-fields";

export interface ContactData {
  name: string;
  email: string;
  phone: string;
  relationship: string;
  consent: true;
}

export interface ContactCallbacks {
  onSubmit: (data: ContactData) => void;
  onBack: () => void;
}

export interface ContactApi {
  /** Toggle the busy state on the submit button (used while the POST is in flight). */
  setSubmitting: (busy: boolean) => void;
}

// A pragmatic email check: non-empty local part, "@", a dotted domain. Not RFC-perfect on purpose;
// the relay validates authoritatively.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RELATIONSHIPS = ["self", "family", "caregiver", "clinician", "other"] as const;

export function renderContact(container: HTMLElement, callbacks: ContactCallbacks): ContactApi {
  while (container.firstChild) container.removeChild(container.firstChild);

  const wrapper = document.createElement("div");
  wrapper.className = "contact";

  const heading = document.createElement("h2");
  heading.textContent = t("ui.contact.title");
  wrapper.appendChild(heading);

  const intro = document.createElement("p");
  intro.className = "help-text";
  intro.textContent = t("ui.contact.intro");
  wrapper.appendChild(intro);

  const form = document.createElement("form");
  form.noValidate = true;

  const nameInput = textField(form, "name", t("ui.contact.name_label"), { required: true });
  const emailInput = textField(form, "email", t("ui.contact.email_label"), { type: "email", required: true });
  const emailError = document.createElement("p");
  emailError.className = "field-error";
  emailError.setAttribute("role", "alert");
  emailError.hidden = true;
  emailError.textContent = t("ui.contact.email_invalid");
  emailInput.parentElement!.appendChild(emailError);

  const phoneInput = textField(form, "phone", t("ui.contact.phone_label"), { type: "tel" });

  const relationshipSelect = selectField(
    form,
    "relationship",
    t("ui.contact.relationship_label"),
    RELATIONSHIPS.map((r) => ({ value: r, label: t(`ui.contact.relationship.${r}`) }))
  );

  const consentGroup = document.createElement("div");
  consentGroup.className = "checkbox-option consent-group";
  const consentInput = document.createElement("input");
  consentInput.type = "checkbox";
  consentInput.name = "consent";
  consentInput.id = "field-consent";
  const consentLabel = document.createElement("label");
  consentLabel.htmlFor = "field-consent";
  consentLabel.textContent = t("ui.contact.consent_label");
  consentGroup.appendChild(consentInput);
  consentGroup.appendChild(consentLabel);
  form.appendChild(consentGroup);

  const actions = document.createElement("div");
  actions.className = "contact-actions";

  const submitBtn = document.createElement("button");
  submitBtn.type = "submit";
  submitBtn.className = "btn-primary";
  submitBtn.dataset.action = "submit";
  submitBtn.textContent = t("ui.contact.submit");
  submitBtn.disabled = true;

  const backBtn = document.createElement("button");
  backBtn.type = "button";
  backBtn.className = "btn-secondary";
  backBtn.dataset.action = "back";
  backBtn.textContent = t("ui.contact.back");
  backBtn.addEventListener("click", () => callbacks.onBack());

  actions.appendChild(submitBtn);
  actions.appendChild(backBtn);
  form.appendChild(actions);
  wrapper.appendChild(form);
  container.appendChild(wrapper);

  let submitting = false;

  function emailValid(): boolean {
    return EMAIL_RE.test(emailInput.value.trim());
  }

  function validate(): void {
    const hasName = nameInput.value.trim().length > 0;
    const emailOk = emailValid();
    const consented = consentInput.checked;
    emailError.hidden = emailInput.value.trim().length === 0 || emailOk;
    submitBtn.disabled = submitting || !(hasName && emailOk && consented);
  }

  form.addEventListener("input", validate);
  form.addEventListener("change", validate);

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    if (submitBtn.disabled) return;
    callbacks.onSubmit({
      name: nameInput.value.trim(),
      email: emailInput.value.trim(),
      phone: phoneInput.value.trim(),
      relationship: relationshipSelect.value,
      consent: true,
    });
  });

  validate();

  return {
    setSubmitting(busy: boolean): void {
      submitting = busy;
      submitBtn.textContent = busy ? t("ui.contact.submitting") : t("ui.contact.submit");
      validate();
    },
  };
}

